/**
 * Layout Panel - Visual Layout Structure Control
 * 
 * Shows hierarchical section/row/column structure with visibility controls
 * and basic style management for layout elements
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Menu,
  MenuItem,
  TextField,
  Collapse,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  DragHandle as DragHandleIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  TableRows as TableRowsIcon,
  ViewColumn as ViewColumnIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Article as ArticleIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardMoveDownIcon,
} from '@mui/icons-material';

import { 
  PageDoc, 
  Section, 
  Row, 
  Column, 
  Style,
  LayoutElement,
  LayoutAction
} from '../../types/page-builder.types';

interface LayoutPanelProps {
  pageDoc: PageDoc;
  selectedElementId?: string;
  selectedElementType?: 'view' | 'section' | 'row' | 'column' | 'block';
  onLayoutAction: (action: LayoutAction) => void;
  onSelectElement: (elementId: string, elementType: 'view' | 'section' | 'row' | 'column') => void;
  readonly?: boolean;
  expandedState?: {
    sections: Set<string>;
    rows: Set<string>;
  };
  onExpandedStateChange?: (state: { sections: Set<string>; rows: Set<string> }) => void;
}

export const LayoutPanel: React.FC<LayoutPanelProps> = ({
  pageDoc,
  selectedElementId,
  selectedElementType,
  onLayoutAction,
  onSelectElement,
  readonly = false,
  expandedState,
  onExpandedStateChange
}) => {
  // Initialize with provided state or all sections and rows expanded by default
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (expandedState?.sections) {
      return expandedState.sections;
    }
    return new Set(pageDoc.layout.map(section => section.id));
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => {
    if (expandedState?.rows) {
      return expandedState.rows;
    }
    const rows = new Set<string>();
    pageDoc.layout.forEach(section => {
      section.rows.forEach(row => {
        rows.add(row.id);
      });
    });
    return rows;
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuContext, setMenuContext] = useState<{
    elementId: string;
    elementType: 'view' | 'section' | 'row' | 'column';
    sectionId?: string;
    rowId?: string;
  } | null>(null);
  
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    elementType: 'section' | 'row' | 'column';
    elementId: string;
    elementName: string;
    childrenCount: number;
    childrenDescription: string;
  }>({
    isOpen: false,
    elementType: 'section',
    elementId: '',
    elementName: '',
    childrenCount: 0,
    childrenDescription: ''
  });

  // Auto-expand new sections and rows
  useEffect(() => {
    const newSections = pageDoc.layout
      .filter(section => !expandedSections.has(section.id))
      .map(section => section.id);
    
    const newRows: string[] = [];
    pageDoc.layout.forEach(section => {
      section.rows.forEach(row => {
        if (!expandedRows.has(row.id)) {
          newRows.push(row.id);
        }
      });
    });

    if (newSections.length > 0 || newRows.length > 0) {
      setExpandedSections(prev => {
        const next = new Set(prev);
        newSections.forEach(id => next.add(id));
        return next;
      });
      setExpandedRows(prev => {
        const next = new Set(prev);
        newRows.forEach(id => next.add(id));
        return next;
      });
    }
  }, [pageDoc.layout]);

  // Notify parent of expansion state changes
  useEffect(() => {
    if (onExpandedStateChange) {
      onExpandedStateChange({
        sections: expandedSections,
        rows: expandedRows
      });
    }
  }, [expandedSections, expandedRows, onExpandedStateChange]);

  const toggleSectionExpanded = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const toggleRowExpanded = useCallback((rowId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    elementId: string,
    elementType: 'section' | 'row' | 'column',
    sectionId?: string,
    rowId?: string
  ) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuContext({ elementId, elementType, sectionId, rowId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuContext(null);
  };

  const handleAddElement = (type: 'section' | 'row' | 'column') => {
    if (!menuContext) return;
    
    switch (type) {
      case 'section':
        onLayoutAction({ type: 'ADD_SECTION' });
        break;
      case 'row':
        if (menuContext.elementType === 'section') {
          onLayoutAction({ 
            type: 'ADD_ROW', 
            sectionId: menuContext.elementId 
          });
        }
        break;
      case 'column':
        if (menuContext.elementType === 'row' && menuContext.sectionId) {
          onLayoutAction({ 
            type: 'ADD_COLUMN', 
            sectionId: menuContext.sectionId,
            rowId: menuContext.elementId 
          });
        }
        break;
    }
    handleMenuClose();
  };

  const analyzeElementDeletion = (elementType: 'section' | 'row' | 'column', elementId: string) => {
    let childrenCount = 0;
    let childrenDescription = '';
    
    if (elementType === 'section') {
      const section = pageDoc.layout.find(s => s.id === elementId);
      if (section) {
        const rowCount = section.rows.length;
        let columnCount = 0;
        let blockCount = 0;
        
        section.rows.forEach(row => {
          columnCount += row.columns.length;
          row.columns.forEach(column => {
            blockCount += column.blocks.length;
          });
        });
        
        childrenCount = rowCount + columnCount + blockCount;
        const parts = [];
        if (rowCount > 0) parts.push(`${rowCount} row${rowCount === 1 ? '' : 's'}`);
        if (columnCount > 0) parts.push(`${columnCount} column${columnCount === 1 ? '' : 's'}`);
        if (blockCount > 0) parts.push(`${blockCount} component${blockCount === 1 ? '' : 's'}`);
        childrenDescription = parts.join(', ');
      }
    } else if (elementType === 'row') {
      let columnCount = 0;
      let blockCount = 0;
      
      for (const section of pageDoc.layout) {
        const row = section.rows.find(r => r.id === elementId);
        if (row) {
          columnCount = row.columns.length;
          row.columns.forEach(column => {
            blockCount += column.blocks.length;
          });
          break;
        }
      }
      
      childrenCount = columnCount + blockCount;
      const parts = [];
      if (columnCount > 0) parts.push(`${columnCount} column${columnCount === 1 ? '' : 's'}`);
      if (blockCount > 0) parts.push(`${blockCount} component${blockCount === 1 ? '' : 's'}`);
      childrenDescription = parts.join(', ');
    } else if (elementType === 'column') {
      let blockCount = 0;
      
      for (const section of pageDoc.layout) {
        for (const row of section.rows) {
          const column = row.columns.find(c => c.id === elementId);
          if (column) {
            blockCount = column.blocks.length;
            break;
          }
        }
      }
      
      childrenCount = blockCount;
      if (blockCount > 0) {
        childrenDescription = `${blockCount} component${blockCount === 1 ? '' : 's'}`;
      }
    }
    
    return { childrenCount, childrenDescription };
  };

  const handleDeleteElement = () => {
    if (!menuContext) return;
    
    // View cannot be deleted
    if (menuContext.elementType === 'view') return;
    
    const { childrenCount, childrenDescription } = analyzeElementDeletion(
      menuContext.elementType as 'section' | 'row' | 'column', 
      menuContext.elementId
    );
    
    // If no children, delete immediately
    if (childrenCount === 0) {
      onLayoutAction({
        type: 'DELETE_ELEMENT',
        elementType: menuContext.elementType as 'section' | 'row' | 'column',
        elementId: menuContext.elementId
      });
      handleMenuClose();
      return;
    }
    
    // Show confirmation dialog for elements with children
    const elementName = `${menuContext.elementType} ${
      menuContext.elementType === 'section' 
        ? pageDoc.layout.findIndex(s => s.id === menuContext.elementId) + 1
        : menuContext.elementType === 'row'
        ? 'in section'
        : 'in row'
    }`;
    
    setDeleteConfirmation({
      isOpen: true,
      elementType: menuContext.elementType as 'section' | 'row' | 'column',
      elementId: menuContext.elementId,
      elementName,
      childrenCount,
      childrenDescription
    });
    
    handleMenuClose();
  };

  const confirmDeletion = () => {
    onLayoutAction({
      type: 'DELETE_ELEMENT',
      elementType: deleteConfirmation.elementType,
      elementId: deleteConfirmation.elementId
    });
    
    setDeleteConfirmation({
      isOpen: false,
      elementType: 'section',
      elementId: '',
      elementName: '',
      childrenCount: 0,
      childrenDescription: ''
    });
  };

  const cancelDeletion = () => {
    setDeleteConfirmation({
      isOpen: false,
      elementType: 'section',
      elementId: '',
      elementName: '',
      childrenCount: 0,
      childrenDescription: ''
    });
  };

  const handleToggleVisibility = (elementId: string, elementType: 'section' | 'row' | 'column') => {
    onLayoutAction({
      type: 'TOGGLE_VISIBILITY',
      elementType,
      elementId
    });
  };

  const handleUpdateColumnWidth = (columnId: string, width: number) => {
    onLayoutAction({
      type: 'UPDATE_COLUMN_WIDTH',
      columnId,
      width
    });
  };

  // Reordering helper functions
  const handleMoveElement = (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down') => {
    if (elementType === 'section') {
      const currentIndex = pageDoc.layout.findIndex(s => s.id === elementId);
      const newPosition = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      // Bounds check
      if (newPosition < 0 || newPosition >= pageDoc.layout.length) return;
      
      onLayoutAction({
        type: 'MOVE_ELEMENT',
        elementType: 'section',
        elementId,
        position: newPosition
      });
    } else if (elementType === 'row') {
      // Find the section containing this row
      for (const section of pageDoc.layout) {
        const currentIndex = section.rows.findIndex(r => r.id === elementId);
        if (currentIndex !== -1) {
          const newPosition = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
          
          // Bounds check
          if (newPosition < 0 || newPosition >= section.rows.length) return;
          
          onLayoutAction({
            type: 'MOVE_ELEMENT',
            elementType: 'row',
            elementId,
            position: newPosition
          });
          break;
        }
      }
    } else if (elementType === 'column') {
      // Find the row containing this column
      for (const section of pageDoc.layout) {
        for (const row of section.rows) {
          const currentIndex = row.columns.findIndex(c => c.id === elementId);
          if (currentIndex !== -1) {
            const newPosition = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
            
            // Bounds check
            if (newPosition < 0 || newPosition >= row.columns.length) return;
            
            onLayoutAction({
              type: 'MOVE_ELEMENT',
              elementType: 'column',
              elementId,
              position: newPosition
            });
            return; // Exit both loops
          }
        }
      }
    }
  };

  // Check if element can be moved in a given direction
  const canMoveElement = (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down'): boolean => {
    if (elementType === 'section') {
      const currentIndex = pageDoc.layout.findIndex(s => s.id === elementId);
      if (direction === 'up') return currentIndex > 0;
      return currentIndex < pageDoc.layout.length - 1;
    } else if (elementType === 'row') {
      for (const section of pageDoc.layout) {
        const currentIndex = section.rows.findIndex(r => r.id === elementId);
        if (currentIndex !== -1) {
          if (direction === 'up') return currentIndex > 0;
          return currentIndex < section.rows.length - 1;
        }
      }
    } else if (elementType === 'column') {
      for (const section of pageDoc.layout) {
        for (const row of section.rows) {
          const currentIndex = row.columns.findIndex(c => c.id === elementId);
          if (currentIndex !== -1) {
            if (direction === 'up') return currentIndex > 0;
            return currentIndex < row.columns.length - 1;
          }
        }
      }
    }
    return false;
  };

  const getSectionIcon = (section: Section) => {
    switch (section.kind) {
      case 'hero': return '🦸';
      case 'funnel': return '📊';
      case 'custom': return '⚙️';
      default: return '📄';
    }
  };

  const isElementSelected = (elementId: string, elementType: 'view' | 'section' | 'row' | 'column') => {
    return selectedElementId === elementId && selectedElementType === elementType;
  };

  const isElementVisible = (element: Section | Row | Column): boolean => {
    return element.visible !== false; // Default to visible if not specified
  };

  return (
    <Paper sx={{ 
      width: 300, 
      borderRadius: 0, 
      borderRight: 1, 
      borderColor: 'divider',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Layout Structure
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {pageDoc.layout.length} sections, {getTotalRows()} rows, {getTotalColumns()} columns
        </Typography>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        minHeight: 0
      }}>
        <Box sx={{ p: 1, pb: 10 }}>
          {/* View Level */}
          <Box
            onClick={() => onSelectElement(pageDoc._id, 'view')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1,
              mb: 1,
              borderRadius: 1,
              backgroundColor: isElementSelected(pageDoc._id, 'view') ? 'primary.lighter' : 'transparent',
              border: 1,
              borderColor: isElementSelected(pageDoc._id, 'view') ? 'primary.main' : 'divider',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: isElementSelected(pageDoc._id, 'view') ? 'primary.lighter' : 'action.hover'
              }
            }}
          >
            <ArticleIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="medium" noWrap>
                View: {pageDoc.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {pageDoc.layout.length} sections
              </Typography>
            </Box>
          </Box>

          {/* Sections */}
          {pageDoc.layout.length === 0 ? (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              ml: 3,
              color: 'text.secondary'
            }}>
              <FolderIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="body2" textAlign="center">
                No layout sections yet
              </Typography>
              {!readonly && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onLayoutAction({ type: 'ADD_SECTION' })}
                  sx={{ mt: 2 }}
                >
                  Add Section
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ ml: 2 }}>
              {pageDoc.layout.map((section, sectionIndex) => (
                <LayoutSectionItem
                  key={section.id}
                  section={section}
                  sectionIndex={sectionIndex}
                  isExpanded={expandedSections.has(section.id)}
                  onToggleExpanded={() => toggleSectionExpanded(section.id)}
                  isSelected={isElementSelected(section.id, 'section')}
                  onSelect={() => onSelectElement(section.id, 'section')}
                  onMenuOpen={handleMenuOpen}
                  onToggleVisibility={() => handleToggleVisibility(section.id, 'section')}
                  readonly={readonly}
                  // Row props
                  expandedRows={expandedRows}
                  onToggleRowExpanded={toggleRowExpanded}
                  selectedElementId={selectedElementId}
                  selectedElementType={selectedElementType}
                  onSelectRow={(rowId: string) => onSelectElement(rowId, 'row')}
                  onSelectColumn={(columnId: string) => onSelectElement(columnId, 'column')}
                  onRowMenuOpen={handleMenuOpen}
                  onColumnMenuOpen={handleMenuOpen}
                  onToggleRowVisibility={handleToggleVisibility}
                  onToggleColumnVisibility={handleToggleVisibility}
                  onUpdateColumnWidth={handleUpdateColumnWidth}
                  onMoveElement={handleMoveElement}
                  canMoveElement={canMoveElement}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Add Section Button */}
      {!readonly && pageDoc.layout.length > 0 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => onLayoutAction({ type: 'ADD_SECTION' })}
          >
            Add Section
          </Button>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuContext?.elementType === 'section' && [
          <MenuItem key="add-row" onClick={() => handleAddElement('row')}>
            <TableRowsIcon sx={{ mr: 1 }} />
            Add Row
          </MenuItem>,
          <Divider key="divider1" />,
        ]}
        
        {menuContext?.elementType === 'row' && [
          <MenuItem key="add-column" onClick={() => handleAddElement('column')}>
            <ViewColumnIcon sx={{ mr: 1 }} />
            Add Column
          </MenuItem>,
          <Divider key="divider2" />,
        ]}
        
        <MenuItem onClick={() => handleAddElement('section')}>
          <FolderIcon sx={{ mr: 1 }} />
          Add Section
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleDeleteElement} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Deletion Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.isOpen}
        onClose={cancelDeletion}
        aria-labelledby="delete-confirmation-title"
        aria-describedby="delete-confirmation-description"
      >
        <DialogTitle id="delete-confirmation-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirmation-description">
            You are about to delete <strong>{deleteConfirmation.elementName}</strong> which contains{' '}
            <strong>{deleteConfirmation.childrenDescription}</strong>.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'medium', color: 'error.main' }}>
            ⚠️ This action cannot be undone. All {deleteConfirmation.childrenCount} nested elements will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeletion} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeletion} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );

  function getTotalRows(): number {
    return pageDoc.layout.reduce((total, section) => total + section.rows.length, 0);
  }

  function getTotalColumns(): number {
    return pageDoc.layout.reduce((total, section) => 
      total + section.rows.reduce((rowTotal, row) => rowTotal + row.columns.length, 0), 0);
  }
};

// Section Item Component
interface LayoutSectionItemProps {
  section: Section;
  sectionIndex: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isSelected: boolean;
  onSelect: () => void;
  onMenuOpen: (
    event: React.MouseEvent<HTMLElement>,
    elementId: string,
    elementType: 'section' | 'row' | 'column',
    sectionId?: string,
    rowId?: string
  ) => void;
  onToggleVisibility: () => void;
  readonly: boolean;
  // Row related props
  expandedRows: Set<string>;
  onToggleRowExpanded: (rowId: string) => void;
  selectedElementId?: string;
  selectedElementType?: 'view' | 'section' | 'row' | 'column' | 'block';
  onSelectRow: (rowId: string) => void;
  onSelectColumn: (columnId: string) => void;
  onRowMenuOpen: (
    event: React.MouseEvent<HTMLElement>,
    elementId: string,
    elementType: 'section' | 'row' | 'column',
    sectionId?: string,
    rowId?: string
  ) => void;
  onColumnMenuOpen: (
    event: React.MouseEvent<HTMLElement>,
    elementId: string,
    elementType: 'section' | 'row' | 'column',
    sectionId?: string,
    rowId?: string
  ) => void;
  onToggleRowVisibility: (elementId: string, elementType: 'section' | 'row' | 'column') => void;
  onToggleColumnVisibility: (elementId: string, elementType: 'section' | 'row' | 'column') => void;
  onUpdateColumnWidth: (columnId: string, width: number) => void;
  // Move handlers
  onMoveElement: (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down') => void;
  canMoveElement: (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down') => boolean;
}

const LayoutSectionItem: React.FC<LayoutSectionItemProps> = ({
  section,
  sectionIndex,
  isExpanded,
  onToggleExpanded,
  isSelected,
  onSelect,
  onMenuOpen,
  onToggleVisibility,
  readonly,
  expandedRows,
  onToggleRowExpanded,
  selectedElementId,
  selectedElementType,
  onSelectRow,
  onSelectColumn,
  onRowMenuOpen,
  onColumnMenuOpen,
  onToggleRowVisibility,
  onToggleColumnVisibility,
  onUpdateColumnWidth,
  onMoveElement,
  canMoveElement,
}) => {
  const getSectionIcon = (section: Section) => {
    switch (section.kind) {
      case 'hero': return '🦸';
      case 'funnel': return '📊';
      case 'custom': return '⚙️';
      default: return '📄';
    }
  };

  const isElementVisible = (element: Section | Row | Column): boolean => {
    return element.visible !== false;
  };

  const isElementSelected = (elementId: string, elementType: 'view' | 'section' | 'row' | 'column') => {
    return selectedElementId === elementId && selectedElementType === elementType;
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={onSelect}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderRadius: 1,
          backgroundColor: isSelected ? 'primary.lighter' : 'transparent',
          border: 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: isSelected ? 'primary.lighter' : 'action.hover'
          }
        }}
      >
        {/* Expand/Collapse */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
          sx={{ mr: 0.5 }}
        >
          {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        {/* Section Icon & Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <Typography sx={{ mr: 1, fontSize: '16px' }}>
            {getSectionIcon(section)}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>
              Section {sectionIndex + 1}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {section.kind} • {section.rows.length} rows
            </Typography>
          </Box>
        </Box>

        {/* Visibility Toggle */}
        {!readonly && (
          <Tooltip title={isElementVisible(section) ? "Hide section" : "Show section"}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility();
              }}
              sx={{ 
                p: 0.5,
                color: isElementVisible(section) ? 'text.primary' : 'text.disabled',
                mr: 0.5 
              }}
            >
              {isElementVisible(section) ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        )}

        {/* Move Up/Down */}
        {!readonly && (
          <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
            <Tooltip title="Move up" placement="left">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMoveElement('section', section.id, 'up')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveElement('section', section.id, 'up');
                  }}
                  sx={{ 
                    p: 0.25,
                    minWidth: 24,
                    height: 20,
                    color: 'text.secondary',
                    '&.Mui-disabled': { color: 'text.disabled' }
                  }}
                >
                  <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down" placement="left">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMoveElement('section', section.id, 'down')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveElement('section', section.id, 'down');
                  }}
                  sx={{ 
                    p: 0.25,
                    minWidth: 24,
                    height: 20,
                    color: 'text.secondary',
                    '&.Mui-disabled': { color: 'text.disabled' }
                  }}
                >
                  <KeyboardMoveDownIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}

        {/* Menu */}
        {!readonly && (
          <IconButton
            size="small"
            onClick={(e) => onMenuOpen(e, section.id, 'section')}
            sx={{ p: 0.5 }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Rows */}
      <Collapse in={isExpanded}>
        <Box sx={{ ml: 2, mt: 1 }}>
          {section.rows.map((row, rowIndex) => (
            <LayoutRowItem
              key={row.id}
              row={row}
              rowIndex={rowIndex}
              sectionId={section.id}
              isExpanded={expandedRows.has(row.id)}
              onToggleExpanded={() => onToggleRowExpanded(row.id)}
              isSelected={isElementSelected(row.id, 'row')}
              onSelect={() => onSelectRow(row.id)}
              onMenuOpen={(e) => onRowMenuOpen(e, row.id, 'row', section.id)}
              onToggleVisibility={() => onToggleRowVisibility(row.id, 'row')}
              readonly={readonly}
              // Column props
              selectedElementId={selectedElementId}
              selectedElementType={selectedElementType}
              onSelectColumn={onSelectColumn}
              onColumnMenuOpen={onColumnMenuOpen}
              onToggleColumnVisibility={onToggleColumnVisibility}
              onUpdateColumnWidth={onUpdateColumnWidth}
              onMoveElement={onMoveElement}
              canMoveElement={canMoveElement}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Row Item Component
interface LayoutRowItemProps {
  row: Row;
  rowIndex: number;
  sectionId: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isSelected: boolean;
  onSelect: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onToggleVisibility: () => void;
  readonly: boolean;
  // Column props
  selectedElementId?: string;
  selectedElementType?: 'view' | 'section' | 'row' | 'column' | 'block';
  onSelectColumn: (columnId: string) => void;
  onColumnMenuOpen: (
    event: React.MouseEvent<HTMLElement>,
    elementId: string,
    elementType: 'section' | 'row' | 'column',
    sectionId?: string,
    rowId?: string
  ) => void;
  onToggleColumnVisibility: (elementId: string, elementType: 'section' | 'row' | 'column') => void;
  onUpdateColumnWidth: (columnId: string, width: number) => void;
  // Move handlers
  onMoveElement: (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down') => void;
  canMoveElement: (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down') => boolean;
}

const LayoutRowItem: React.FC<LayoutRowItemProps> = ({
  row,
  rowIndex,
  sectionId,
  isExpanded,
  onToggleExpanded,
  isSelected,
  onSelect,
  onMenuOpen,
  onToggleVisibility,
  readonly,
  selectedElementId,
  selectedElementType,
  onSelectColumn,
  onColumnMenuOpen,
  onToggleColumnVisibility,
  onUpdateColumnWidth,
  onMoveElement,
  canMoveElement,
}) => {
  const isElementVisible = (element: Section | Row | Column): boolean => {
    return element.visible !== false;
  };

  const isElementSelected = (elementId: string, elementType: 'view' | 'section' | 'row' | 'column') => {
    return selectedElementId === elementId && selectedElementType === elementType;
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onClick={onSelect}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderRadius: 1,
          backgroundColor: isSelected ? 'secondary.lighter' : 'transparent',
          border: 1,
          borderColor: isSelected ? 'secondary.main' : 'divider',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: isSelected ? 'secondary.lighter' : 'action.hover'
          }
        }}
      >
        {/* Expand/Collapse */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
          sx={{ mr: 0.5 }}
        >
          {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>

        {/* Row Icon & Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <TableRowsIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>
              Row {rowIndex + 1}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.columns.length} columns
            </Typography>
          </Box>
        </Box>

        {/* Visibility Toggle */}
        {!readonly && (
          <Tooltip title={isElementVisible(row) ? "Hide row" : "Show row"}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility();
              }}
              sx={{ 
                p: 0.5,
                color: isElementVisible(row) ? 'text.primary' : 'text.disabled',
                mr: 0.5 
              }}
            >
              {isElementVisible(row) ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        )}

        {/* Move Up/Down */}
        {!readonly && (
          <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
            <Tooltip title="Move up" placement="left">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMoveElement('row', row.id, 'up')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveElement('row', row.id, 'up');
                  }}
                  sx={{ 
                    p: 0.25,
                    minWidth: 24,
                    height: 20,
                    color: 'text.secondary',
                    '&.Mui-disabled': { color: 'text.disabled' }
                  }}
                >
                  <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down" placement="left">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMoveElement('row', row.id, 'down')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveElement('row', row.id, 'down');
                  }}
                  sx={{ 
                    p: 0.25,
                    minWidth: 24,
                    height: 20,
                    color: 'text.secondary',
                    '&.Mui-disabled': { color: 'text.disabled' }
                  }}
                >
                  <KeyboardMoveDownIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}

        {/* Menu */}
        {!readonly && (
          <IconButton size="small" onClick={onMenuOpen} sx={{ p: 0.5 }}>
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Columns */}
      <Collapse in={isExpanded}>
        <Box sx={{ ml: 2, mt: 1 }}>
          {row.columns.map((column, columnIndex) => (
            <LayoutColumnItem
              key={column.id}
              column={column}
              columnIndex={columnIndex}
              row={row}
              sectionId={sectionId}
              rowId={row.id}
              isSelected={isElementSelected(column.id, 'column')}
              onSelect={() => onSelectColumn(column.id)}
              onMenuOpen={(e) => onColumnMenuOpen(e, column.id, 'column', sectionId, row.id)}
              onToggleVisibility={() => onToggleColumnVisibility(column.id, 'column')}
              onUpdateWidth={onUpdateColumnWidth}
              readonly={readonly}
              onMoveElement={onMoveElement}
              canMoveElement={canMoveElement}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Column Item Component
interface LayoutColumnItemProps {
  column: Column;
  columnIndex: number;
  row: Row;
  sectionId: string;
  rowId: string;
  isSelected: boolean;
  onSelect: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onToggleVisibility: () => void;
  onUpdateWidth: (columnId: string, width: number) => void;
  readonly: boolean;
  // Move handlers
  onMoveElement: (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down') => void;
  canMoveElement: (elementType: 'section' | 'row' | 'column', elementId: string, direction: 'up' | 'down') => boolean;
}

const LayoutColumnItem: React.FC<LayoutColumnItemProps> = ({
  column,
  columnIndex,
  row,
  sectionId,
  rowId,
  isSelected,
  onSelect,
  onMenuOpen,
  onToggleVisibility,
  onUpdateWidth,
  readonly,
  onMoveElement,
  canMoveElement,
}) => {
  const isElementVisible = (element: Section | Row | Column): boolean => {
    return element.visible !== false;
  };

  // Calculate available width for this column
  const MAX_WIDTH = 12;
  const otherColumnsWidth = row.columns.reduce((sum, col) => {
    return col.id === column.id ? sum : sum + (col.width || 12);
  }, 0);
  const maxAvailableWidth = MAX_WIDTH - otherColumnsWidth;
  const totalRowWidth = row.columns.reduce((sum, col) => sum + (col.width || 12), 0);

  return (
    <Box>
      <Box
        onClick={onSelect}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          mb: 0.5,
          borderRadius: 1,
          backgroundColor: isSelected ? 'success.lighter' : 'transparent',
          border: 1,
          borderColor: isSelected ? 'success.main' : 'divider',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: isSelected ? 'success.lighter' : 'action.hover'
          }
        }}
      >
      {/* Column Icon & Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        <ViewColumnIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            Column {columnIndex + 1}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {column.blocks.length} blocks • {Math.round((column.width || 12) / 12 * 100)}%
          </Typography>
        </Box>
      </Box>

      {/* Visibility Toggle */}
      {!readonly && (
        <Tooltip title={isElementVisible(column) ? "Hide column" : "Show column"}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility();
            }}
            sx={{ 
              p: 0.5,
              color: isElementVisible(column) ? 'text.primary' : 'text.disabled',
              mr: 0.5 
            }}
          >
            {isElementVisible(column) ? <VisibilityIcon sx={{ fontSize: 18 }} /> : <VisibilityOffIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      )}

      {/* Move Left/Right */}
      {!readonly && (
        <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
          <Tooltip title="Move left" placement="left">
            <span>
              <IconButton
                size="small"
                disabled={!canMoveElement('column', column.id, 'up')}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveElement('column', column.id, 'up');
                }}
                sx={{ 
                  p: 0.25,
                  minWidth: 24,
                  height: 20,
                  color: 'text.secondary',
                  '&.Mui-disabled': { color: 'text.disabled' }
                }}
              >
                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move right" placement="left">
            <span>
              <IconButton
                size="small"
                disabled={!canMoveElement('column', column.id, 'down')}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveElement('column', column.id, 'down');
                }}
                sx={{ 
                  p: 0.25,
                  minWidth: 24,
                  height: 20,
                  color: 'text.secondary',
                  '&.Mui-disabled': { color: 'text.disabled' }
                }}
              >
                <KeyboardMoveDownIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Menu */}
      {!readonly && (
        <IconButton size="small" onClick={onMenuOpen} sx={{ p: 0.5 }}>
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      </Box>

      {/* Width Controls (show when selected) */}
    {isSelected && !readonly && (
      <Box sx={{ mt: 1, ml: 4, mr: 1, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Column Width: {Math.round((column.width || 12) / 12 * 100)}%
          </Typography>
          <Typography
            variant="caption"
            color={totalRowWidth > MAX_WIDTH ? 'error.main' : 'success.main'}
            sx={{ fontWeight: 'medium' }}
          >
            Row: {totalRowWidth}/{MAX_WIDTH} units
          </Typography>
        </Box>

        {/* Show warning if row is over-allocated */}
        {totalRowWidth > MAX_WIDTH && (
          <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
            <Typography variant="caption">
              Row exceeds {MAX_WIDTH} units. Reduce column widths.
            </Typography>
          </Alert>
        )}

        {/* Width adjustment controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {/* Decrement button */}
          <IconButton
            size="small"
            disabled={(column.width || 12) <= 1}
            onClick={() => onUpdateWidth(column.id, (column.width || 12) - 1)}
            sx={{
              border: 1,
              borderColor: 'divider',
              '&.Mui-disabled': { borderColor: 'action.disabledBackground' }
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Slider */}
          <Slider
            size="small"
            value={column.width || 12}
            min={1}
            max={maxAvailableWidth}
            step={1}
            onChange={(_, newValue) => {
              onUpdateWidth(column.id, newValue as number);
            }}
            valueLabelFormat={(value) => `${value} (${Math.round(value / 12 * 100)}%)`}
            valueLabelDisplay="auto"
            marks={[
              { value: 1, label: '' },
              { value: Math.min(Math.floor(maxAvailableWidth / 2), maxAvailableWidth), label: '' },
              { value: maxAvailableWidth, label: '' }
            ].filter((mark, idx, arr) => arr.findIndex(m => m.value === mark.value) === idx && mark.value >= 1)}
            sx={{ flex: 1 }}
          />

          {/* Increment button */}
          <IconButton
            size="small"
            disabled={(column.width || 12) >= maxAvailableWidth}
            onClick={() => onUpdateWidth(column.id, (column.width || 12) + 1)}
            sx={{
              border: 1,
              borderColor: 'divider',
              '&.Mui-disabled': { borderColor: 'action.disabledBackground' }
            }}
          >
            <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Number input */}
          <TextField
            size="small"
            type="number"
            value={column.width || 12}
            onChange={(e) => {
              const width = Math.min(maxAvailableWidth, Math.max(1, parseInt(e.target.value) || 1));
              onUpdateWidth(column.id, width);
            }}
            inputProps={{
              min: 1,
              max: maxAvailableWidth,
              style: { textAlign: 'center' }
            }}
            sx={{ width: 60 }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', display: 'block' }}>
          Max: {maxAvailableWidth} units ({Math.round(maxAvailableWidth / 12 * 100)}%) • Total row limit: {MAX_WIDTH} units
        </Typography>
      </Box>
    )}
    </Box>
  );
};

export default LayoutPanel;